import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { mono, sans, serif } from '../fonts.js';
import { formatCount, formatMoneyMinor, pickPrice, } from './format.js';
import { Tooltip } from './Tooltip.js';
import { CheckMark } from './icons.js';
function limit(plan, dim) {
    const r = plan.limits.find((l) => l.dimension === dim);
    return r === undefined ? null : r.limit_value;
}
/** The kicker over the agency tier list. Prefers the bundled
 *  `included_site_plan_name` from the catalog (that's what each
 *  client site runs); falls back to the retail paid-site plan name
 *  the page shows above; last resort "Agency plans". */
export function buildAgencyKicker(agencyPlans, fallbackPlanName) {
    const included = agencyPlans.find((p) => p.included_site_plan_name !== null)
        ?.included_site_plan_name;
    const planName = included ?? fallbackPlanName;
    if (planName === null || planName.length === 0)
        return 'Agency plans';
    return `Plans · ${planName} on every client site`;
}
export function AgencyBand({ agencyPlans, sitePlans, theme, interval, copy, agenciesHref, monthsFreeOnYearly = 2, talkHref, }) {
    const paidSitePlan = sitePlans.find((p) => (pickPrice(p.prices, 'monthly')?.amount_minor ?? 0) > 0);
    const retailPerSite = paidSitePlan === undefined
        ? null
        : pickPrice(paidSitePlan.prices, 'monthly')?.amount_minor ?? null;
    const grid = {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.1fr)',
        border: `1px solid ${theme.border}`,
        borderRadius: 4,
        overflow: 'clip',
        background: theme.surface,
        scrollMarginTop: 24,
    };
    const copyCol = {
        padding: 30,
        display: 'grid',
        gap: 14,
        alignContent: 'start',
    };
    const tiersCol = {
        background: theme.surface2,
        borderLeft: `1px solid ${theme.border}`,
        padding: '26px 22px',
        display: 'grid',
        gap: 12,
        alignContent: 'start',
    };
    // Common left-hand copy
    const lead = (_jsxs("div", { style: copyCol, children: [_jsx("span", { style: {
                    ...mono,
                    fontSize: 11,
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    color: theme.muted,
                }, children: copy.agencyBand.kicker }), _jsx("h2", { style: {
                    ...serif,
                    fontWeight: 400,
                    fontSize: 32,
                    lineHeight: 1.08,
                    margin: 0,
                    color: theme.text,
                }, children: copy.agencyBand.heading }), _jsx("p", { style: { ...sans, fontSize: 14, color: theme.body, margin: 0 }, children: copy.agencyBand.lede }), _jsx("ul", { style: {
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    display: 'grid',
                    gap: 9,
                    fontSize: 14,
                }, children: copy.agencyBand.features.map((f) => (_jsxs("li", { style: { display: 'grid', gridTemplateColumns: '16px 1fr', gap: 8, ...sans, color: theme.body }, children: [_jsx("span", { style: { marginTop: 3, color: theme.limeFg }, children: _jsx(CheckMark, {}) }), _jsx(Tooltip, { tip: f.tooltip, theme: theme, children: f.label })] }, f.label))) })] }));
    if (agencyPlans.length === 0) {
        const href = talkHref ?? agenciesHref;
        return (_jsxs("section", { id: "agency", className: "pr-agency", style: grid, children: [lead, _jsxs("div", { style: tiersCol, children: [_jsx("span", { style: {
                                ...mono,
                                fontSize: 11,
                                letterSpacing: 2,
                                textTransform: 'uppercase',
                                color: theme.muted,
                            }, children: copy.agencyBand.talkSubHeading }), _jsx("div", { style: { border: `1px solid ${theme.border}`, borderRadius: 3, background: theme.surface }, children: _jsxs("div", { style: {
                                    borderLeft: `3px solid ${theme.limeFg}`,
                                    padding: '20px 20px 20px 22px',
                                    display: 'grid',
                                    gap: 10,
                                }, children: [_jsx("div", { style: { ...sans, color: theme.text, fontWeight: 500, fontSize: 18 }, children: copy.agencyBand.talkTitle }), _jsx("div", { style: { ...sans, fontSize: 13, color: theme.muted }, children: copy.agencyBand.talkMeta }), _jsx("a", { href: href, style: {
                                            ...sans,
                                            justifySelf: 'start',
                                            display: 'inline-block',
                                            background: theme.lime,
                                            color: theme.ink,
                                            padding: '9px 22px',
                                            borderRadius: 2,
                                            border: `1px solid ${theme.lime}`,
                                            textDecoration: 'none',
                                            fontSize: 13,
                                            fontWeight: 500,
                                        }, children: copy.agencyBand.talkCta })] }) })] })] }));
    }
    const recommendedIdx = agencyPlans.length >= 3 ? 1 : agencyPlans.length === 2 ? 1 : 0;
    return (_jsxs("section", { id: "agency", className: "pr-agency", style: grid, children: [lead, _jsxs("div", { style: tiersCol, children: [_jsx("span", { style: {
                            ...mono,
                            fontSize: 11,
                            letterSpacing: 2,
                            textTransform: 'uppercase',
                            color: theme.muted,
                        }, children: buildAgencyKicker(agencyPlans, paidSitePlan?.name ?? null) }), _jsx("div", { style: {
                            border: `1px solid ${theme.border}`,
                            borderRadius: 3,
                            background: theme.surface,
                        }, children: agencyPlans.map((ag, idx) => {
                            const monthly = pickPrice(ag.prices, 'monthly');
                            const yearlyPrice = pickPrice(ag.prices, 'yearly');
                            const activePrice = pickPrice(ag.prices, interval);
                            let shownMonthly = activePrice?.amount_minor ?? 0;
                            if (interval === 'yearly'
                                && yearlyPrice !== undefined
                                && yearlyPrice.amount_minor > 0) {
                                const effectiveMonths = Math.max(1, 12 - monthsFreeOnYearly);
                                shownMonthly = Math.round(yearlyPrice.amount_minor / effectiveMonths);
                            }
                            const includedSites = limit(ag, 'included_sites');
                            const scans = limit(ag, 'agency_scans_monthly');
                            const perSite = includedSites !== null && includedSites > 0
                                ? shownMonthly / includedSites
                                : null;
                            const off = retailPerSite !== null && retailPerSite > 0 && perSite !== null
                                ? Math.round((1 - perSite / retailPerSite) * 100)
                                : null;
                            const whiteLabel = ag.features.includes('white_label');
                            const currency = activePrice?.currency ?? 'usd';
                            const isRec = idx === recommendedIdx;
                            const metaBits = [
                                includedSites !== null ? `${includedSites} client sites` : null,
                                scans !== null ? `${formatCount(scans)} scans a month` : null,
                                whiteLabel ? 'white label' : null,
                            ].filter((v) => v !== null);
                            return (_jsxs("div", { className: "pr-ag-tier", style: {
                                    display: 'grid',
                                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                                    gap: '4px 14px',
                                    padding: '14px 16px 14px 18px',
                                    borderBottom: idx === agencyPlans.length - 1
                                        ? '0'
                                        : `1px solid ${theme.borderSoft}`,
                                    position: 'relative',
                                    borderLeft: isRec ? `3px solid ${theme.limeFg}` : undefined,
                                    paddingLeft: isRec ? 15 : 18,
                                }, children: [_jsxs("span", { style: { ...sans, color: theme.text, fontWeight: 500, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }, children: [ag.name, isRec ? (_jsx("span", { style: {
                                                    ...mono,
                                                    fontSize: 10,
                                                    letterSpacing: 1.6,
                                                    textTransform: 'uppercase',
                                                    background: theme.lime,
                                                    color: theme.ink,
                                                    padding: '2px 6px',
                                                    borderRadius: 2,
                                                }, children: "Recommended" })) : null] }), _jsxs("span", { style: {
                                            ...serif,
                                            fontWeight: 600,
                                            fontSize: 22,
                                            color: theme.text,
                                            letterSpacing: -0.5,
                                            textAlign: 'right',
                                            fontVariantNumeric: 'tabular-nums',
                                            lineHeight: 1.1,
                                        }, children: [monthly === undefined ? '—' : formatMoneyMinor(shownMonthly, currency), _jsxs("span", { style: { ...sans, fontSize: 12, fontWeight: 400, color: theme.muted }, children: [' ', "a month"] })] }), metaBits.length > 0 ? (_jsx("span", { style: { ...sans, fontSize: 13, color: theme.muted, fontVariantNumeric: 'tabular-nums' }, children: metaBits.join(' · ') })) : (_jsx("span", {})), perSite !== null ? (_jsxs("span", { style: { ...sans, fontSize: 14, color: theme.text, fontWeight: 500, textAlign: 'right', whiteSpace: 'nowrap' }, children: [formatMoneyMinor(Math.round(perSite), currency), " a site", off !== null ? (_jsxs("span", { style: { color: theme.muted, fontWeight: 400 }, children: [" \u00B7 ", off, "% off"] })) : null] })) : (_jsx("span", {}))] }, ag.plan_id));
                        }) }), retailPerSite !== null ? (_jsxs("span", { style: { ...sans, fontSize: 13, color: theme.muted }, children: ["Retail ", paidSitePlan?.name, " is ", formatMoneyMinor(retailPerSite, agencyPlans[0]?.prices[0]?.currency ?? 'usd'), " a site."] })) : null, _jsx("a", { href: agenciesHref, style: {
                            ...sans,
                            justifySelf: 'start',
                            display: 'inline-block',
                            color: theme.text,
                            fontSize: 13,
                            textDecoration: 'underline',
                            textUnderlineOffset: 3,
                        }, children: "Talk to us" })] })] }));
}
//# sourceMappingURL=AgencyBand.js.map